import React, { useState, useEffect } from 'react';
import { Input, Spin, Segmented, Alert } from 'antd';
import { SearchOutlined, AppstoreOutlined, TableOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as API from '../services/api';
import { useThemeStyles } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  ProjectCardGrid,
  ProjectCardPagination,
  ProjectTableView,
} from '../components/ProjectDirectoryViews';
import CreateProjectFab from '../components/CreateProjectFab';
import PageHeader from '../components/PageHeader';
import StarterKitIssuanceModal from '../components/StarterKitIssuanceModal';
import { useStarterKitIssuance } from '../starter_kit/useStarterKitIssuance';
import { pageShellStyle } from '../theme';

const ViewProjects = () => {
  const navigate = useNavigate();
  const themeStyles = useThemeStyles();
  const { token, isAdmin, isSuperAdmin } = useAuth();
  const isGroupAdmin = isAdmin && !isSuperAdmin;
  const isEndUser = !isAdmin;
  const starterKit = useStarterKitIssuance(token);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectLayout, setProjectLayout] = useState('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    if (token) {
      starterKit.refreshEntitlements();
    }
  }, [token, starterKit.refreshEntitlements]);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    const fetchProjects = async () => {
      setLoading(true);
      try {
        if (!token) {
          if (alive) setProjects([]);
          return;
        }
        const response = await API.listRbacProjects(token, null, { signal: controller.signal });
        const data = Array.isArray(response.data) ? response.data : [];
        const projectsWithStatus = data
          .map((p) => {
            const meta = p.meta || {};
            const deployedFromMeta =
              meta.embedding_status === 'completed' && meta.vector_status === 'completed';
            return {
              ...p,
              created_at: meta.created_at || meta.updated_at || null,
              embedding_status: meta.embedding_status || 'pending',
              vector_status: meta.vector_status || 'pending',
              deleted: meta.deleted || false,
              deployed: p.chat_available != null ? Boolean(p.chat_available) : deployedFromMeta,
            };
          })
          .filter((p) => !p.deleted)
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        if (alive) setProjects(projectsWithStatus);
      } catch (error) {
        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          console.error('Error fetching projects:', error);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchProjects();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [token]);

  const filteredProjects = projects.filter((p) => {
    const q = (searchQuery || '').toLowerCase().trim();
    if (!q) return true;
    return String(p.name || '').toLowerCase().includes(q)
      || String(p.project_id || '').toLowerCase().includes(q);
  });

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + pageSize);

  const openProject = (project) => {
    navigate(`/project/${project.project_id}?tab=file_pipeline`);
  };

  const openChat = (project) => {
    navigate(`/chat/${project.project_id}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ ...pageShellStyle, display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title="All Projects"
        actions={(
          <Segmented
            value={projectLayout}
            onChange={(value) => {
              setProjectLayout(value);
              setCurrentPage(1);
            }}
            options={[
              { label: 'Cards', value: 'cards', icon: <AppstoreOutlined /> },
              { label: 'Table', value: 'table', icon: <TableOutlined /> },
            ]}
          />
        )}
      />
      <div style={{ marginBottom: 16, width: '100%' }}>
        <Input
          placeholder="Search projects..."
          prefix={<SearchOutlined style={{ color: themeStyles.textTertiary }} />}
          style={{ width: '100%', maxWidth: 560 }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          allowClear
          onClear={() => {
            setSearchQuery('');
            setSearchText('');
            setCurrentPage(1);
          }}
        />
      </div>

      {!loading && isEndUser && projects.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="You have no projects assigned. Contact your admin."
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && isGroupAdmin && projects.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="You have not created any projects yet."
          description="Use the + button to create your first project."
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && isSuperAdmin && projects.length === 0 && (
        <Alert
          type="info"
          showIcon
          message="There are no projects to display yet."
          style={{ marginBottom: 16 }}
        />
      )}

      {filteredProjects.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: themeStyles.textSecondary }}>
          No results found
        </div>
      ) : projectLayout === 'table' ? (
        <ProjectTableView
          projects={filteredProjects}
          themeStyles={themeStyles}
          onOpenProject={openProject}
          showAdminColumn={isSuperAdmin}
          onDownloadStarterKit={starterKit.startIssuance}
          starterKitBusyProjectId={starterKit.creatingProjectId}
          getProjectKitState={starterKit.getProjectKitState}
          getProjectKitIconVisualState={starterKit.getProjectKitIconVisualState}
          getStarterKitIconTooltip={starterKit.getStarterKitIconTooltip}
          pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
        />
      ) : (
        <>
          <ProjectCardGrid
            projects={paginatedProjects}
            themeStyles={themeStyles}
            onOpenProject={openProject}
            onOpenChat={openChat}
            onDownloadStarterKit={starterKit.startIssuance}
            starterKitBusyProjectId={starterKit.creatingProjectId}
            getProjectKitState={starterKit.getProjectKitState}
            getProjectKitIconVisualState={starterKit.getProjectKitIconVisualState}
            getStarterKitIconTooltip={starterKit.getStarterKitIconTooltip}
            showChatShortcut={!isSuperAdmin}
            showAdmin={isSuperAdmin}
          />
          <ProjectCardPagination
            current={currentPage}
            total={filteredProjects.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
          />
        </>
      )}
      <StarterKitIssuanceModal
        open={starterKit.modalOpen}
        entitlement={starterKit.entitlement}
        isRegenerateFlow={starterKit.isRegenerateFlow}
        downloading={starterKit.downloading}
        onCancel={starterKit.closeModal}
        onDownload={starterKit.downloadIssuedKit}
      />
      <CreateProjectFab />
    </div>
  );
};

export default ViewProjects;
